import { cn } from '@/lib/utils'
import type { ConciergeToolCallRecord } from '@/stores/useConciergeStore'

interface Props {
  call: ConciergeToolCallRecord
}

/**
 * Render one tool invocation as a small inline pill. Mirrors the
 * Obsidian plugin's "Tool · <name> · <input-summary> · <result-summary>"
 * line. Three states — running (pulsing dot), success (green dot),
 * error (red dot).
 *
 * Result summary is pre-formatted on the backend by `_summarize_result`
 * (e.g. "3 row(s)", "ok", "completed") — we render it verbatim. Input
 * summary is computed client-side from the well-known input-key shape
 * per tool so the pill is useful while the call is still running and
 * we don't yet have a result string.
 */
export function ConciergeToolPill({ call }: Props) {
  const inputSummary = summarizeInput(call.name, call.input)
  const resultSummary =
    call.status === 'running'
      ? '…'
      : (call.summary && call.summary.trim()) ||
        (call.status === 'error' ? 'error' : 'ok')

  return (
    <div className="flex items-baseline gap-2 text-[11px] font-light text-muted-foreground py-0.5">
      <span
        className={cn(
          'inline-block w-1.5 h-1.5 rounded-full translate-y-[1px] shrink-0',
          call.status === 'running' && 'bg-amber-500 animate-pulse',
          call.status === 'success' && 'bg-green-500',
          call.status === 'error' && 'bg-red-500'
        )}
      />
      <span className="uppercase tracking-[0.08em] text-[10px] text-muted-foreground/70">
        tool
      </span>
      <span className="font-mono text-foreground/80 truncate">{call.name}</span>
      {inputSummary && (
        <span className="font-mono text-muted-foreground/70 truncate min-w-0">
          {inputSummary}
        </span>
      )}
      <span className="text-muted-foreground/50">·</span>
      <span
        className={cn(
          'truncate',
          call.status === 'error' && 'text-red-500'
        )}
      >
        {resultSummary}
      </span>
    </div>
  )
}

/**
 * Per-tool input rendering — same shape as the plugin's
 * `summarizeToolInput()`. Conservative defaults: when we don't have a
 * tool-specific summary, pick a few plausibly-interesting keys.
 */
function summarizeInput(name: string, input: Record<string, unknown>): string {
  if (!input || typeof input !== 'object') return ''
  switch (name) {
    case 'block_query':
      return formatKv(input, [
        'project_path',
        'name_contains',
        'status',
        'score',
        'upcoming',
        'days_ahead',
        'limit',
      ])
    case 'semantic_search':
      return formatKv(input, ['query', 'project_path', 'limit'])
    case 'read_vault_file':
      return formatKv(input, ['path'])
    case 'list_cards':
      return formatKv(input, ['project_path', 'block_path', 'name_contains', 'ext'])
    case 'create_block':
      return formatKv(input, [
        'title',
        'type',
        'date',
        'time',
        'duration_minutes',
      ])
    case 'update_block_section':
      return formatKv(input, ['section', 'block_path'])
    case 'update_block_frontmatter':
      return formatKv(input, ['key', 'value', 'block_path'])
    case 'archive_block':
    case 'delete_block':
    case 'move_block':
      return formatKv(input, ['block_path', 'new_project_path'])
    case 'capture_block_outcome':
      return formatKv(input, ['score', 'block_path'])
    case 'reschedule_block':
      return formatKv(input, ['block_path', 'new_date', 'new_time'])
    case 'get_calendar_conflicts':
    case 'find_free_slots':
      return formatKv(input, ['date', 'time', 'duration_minutes'])
    case 'time_distribution':
    case 'time_tracker':
      return formatKv(input, ['window_days', 'group_by', 'project_path'])
    default:
      return formatKv(input, Object.keys(input).slice(0, 2))
  }
}

function formatKv(input: Record<string, unknown>, keys: string[]): string {
  const parts: string[] = []
  for (const k of keys) {
    if (!(k in input)) continue
    const v = input[k]
    if (v === undefined || v === null || v === '') continue
    const sv = typeof v === 'string' ? `"${truncate(v, 30)}"` : String(v)
    parts.push(`${k}=${sv}`)
  }
  return parts.join(' ')
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + '…'
}
